// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import MapPin from "@insite/mobius/Icons/MapPin";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    // <XNG-Changes>
    let fulfillmentLabel = "";
    let fulfillmentValue = "";
    const session = getSession(state);
    const fulfillment = session.fulfillmentMethod;
    if (fulfillment === FulfillmentMethod.Ship) {
        const shipTo = getCurrentShipToState(state).value;

        if (shipTo) {
            const { address1 = "", customerNumber = "" } = getCurrentShipToState(state).value || {};

            fulfillmentLabel = translate("Location:");
            fulfillmentValue = `${address1 || `#${customerNumber}`}`;
        } else {
            fulfillmentLabel = `${translate("Location")}`;
        }
    } else if (fulfillment === FulfillmentMethod.PickUp) {
        // XNG-Change - description instead of name
        const { description = "", address1 = "" } = state.context.session.pickUpWarehouse || {};
        fulfillmentLabel = translate("Pick Up at:");
        fulfillmentValue = `${description || address1}`;
    }
    // </XNG-Changes>
    return {
        fulfillmentLabel,
        fulfillmentValue,
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        isAuthenticated: xIsAuthenticated(state),
        // XNG-Change
        showPickUpOption: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        displayChangeCustomerLink: session.displayChangeCustomerLink,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
    // XNG-Changes
    setNavDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    // XNG-Changes
    labelTypography?: TypographyPresentationProps;
    textWrapper?: InjectableCss;
    changeIcon?: IconPresentationProps;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
            width: calc(100% - 10px);
            padding: 6px 0;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
    // XNG-Change
    extendedStyles: HeaderShipToAddressStyles;
    renderModal?: boolean;
    renderLink?: boolean;
}

const HeaderShipToAddress: FC<Props> = ({
    id,
    fulfillmentLabel,
    fulfillmentValue,
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    isPunchOutSession,
    // XNG-Change
    extendedStyles,
    isAuthenticated,
    setNavDrawerIsOpen,
    renderModal = true,
    renderLink = true,
    displayChangeCustomerLink,
    showPickUpOption,
}) => {
    // XNG-Changes
    React.useEffect(() => {
        if (isDrawerOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    }, [isDrawerOpen]);

    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };
    // XNG-Change ExtendedStyles
    const [styles] = React.useState(() => mergeWithCss(headerShipToAddressStyles, extendedStyles));

    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    // XNG-Change don't show the link in case 1 bill-to 1 ship to and no pickup.
    if (!displayChangeCustomerLink && !showPickUpOption && !isPunchOutSession) {
        return null;
    }

    if (!fulfillmentLabel || !isAuthenticated) {
        return null;
    }

    // XNG-Change:change structure and label
    return (
        <>
            {renderLink && (
                <StyledWrapper {...styles.shipToAddressWrapper}>
                    <Clickable
                        {...styles.titleClickable}
                        onClick={handleClickAddress}
                        data-test-selector="header_shipToButton"
                    >
                        {/* As this doesn't look final, but is the pattern on which the mobile nav drawer was based,
                    please also make changes to `NavigationDrawer.tsx` when you change this. */}
                        <StyledWrapper {...styles.textWrapper}>
                            <Icon {...styles.titleIcon} />
                            <Typography {...styles.labelTypography}>{fulfillmentLabel}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.textWrapper}>
                            <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                                {fulfillmentValue}
                            </Typography>
                            <Icon {...styles.changeIcon} className="edit-icon" />
                        </StyledWrapper>
                    </Clickable>
                </StyledWrapper>
            )}
            {renderModal && (
                <Modal
                    data-test-selector="addressModal"
                    isOpen={isDrawerOpen}
                    headline={isPunchOutSession ? "" : translate("Change Fulfillment Method")}
                    handleClose={handleCloseDrawer}
                    sizeVariant="medium"
                >
                    {createWidgetElement("Header/AddressDrawerContainer", {
                        fields: {},
                    })}
                </Modal>
            )}
            {/* <Drawer
                {...drawerStyles}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
            </Drawer> */}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
